import { Button, makeStyles } from "@material-ui/core";
import React, { useCallback, useEffect, useRef } from "react";
import { useState } from "react";
import { FileInput, ImageField, useNotify } from "react-admin";
import { useField } from "react-final-form";
import ReactCrop from "react-image-crop";
import "react-image-crop/lib/ReactCrop.scss";

interface Props {
  source: string;
  [x: string]: any;
}

const useStyles = makeStyles({
  main: {
    maxWidth: "600px",
    maxHeight: "80vh",
    overflowY: "scroll",
  },
  topNavsContainer: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "1.4rem",
  },
});

const CustomFileInput = ({
  source,
  relationSource,
  label,
  ...props
}: Props) => {
  const {
    input: { onChange, value },
  } = useField(source);
  const notify = useNotify();
  const ownInput = useField(`${source}File`);
  const [fileCache, setFileCache] = useState([]);
  const [isImageValid, setIsImageValid] = useState(true);
  const [isImageCancel, setIsImageCancel] = useState(false);

  const [upImg, setUpImg] = useState(null);
  const imgRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const [crop, setCrop] = useState({ unit: "%", width: 30, aspect: 1 / 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [imageCropped, setImageCropped] = useState(null);
  const classes = useStyles();

  //on render, format existing value(from api) to adapt with file input format
  useEffect(() => {
    //used when existing record available, in case of show/update. map the data to input file data source
    if (value) {
      const newFiles = [
        {
          id: value.id,
          title: value.title ? value.title : value,
          url: value.url ? value.url : value,
          type: value.type,
        },
      ];
      setFileCache(newFiles);
    }
  }, [value]);

  //use for set ownInput value
  useEffect(() => {
    if (isImageValid) {
      ownInput.input.onChange(fileCache);
    }
  }, [fileCache]);

  //use for check image is invalid
  //if invalid, set input to null
  useEffect(() => {
    if (!isImageValid) {
      ownInput.input.onChange(null);
      onChange(null);
    }
  }, [isImageValid]);

  const validateImageSize = (size: number) => {
    //Validate Image Size only max 2MB
    if (size > 2100000) {
      return false;
    }

    return true;
  };

  const onLoad = useCallback((img) => {
    imgRef.current = img;
  }, []);

  const onCancel = () => {
    ownInput.input.onChange(null);
    onChange(null);
    setIsImageCancel(true);
  };

  useEffect(() => {
    if (!completedCrop || !previewCanvasRef.current || !imgRef.current) {
      return;
    }

    const image = imgRef.current;
    const canvas = previewCanvasRef.current;
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const ctx = canvas.getContext("2d");
    const pixelRatio = window.devicePixelRatio;

    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;

    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = "high";

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width,
      crop.height
    );

    canvas.toBlob(
      async (blob: any) => {
        if (blob) {
          let url = await getBlobFromUrl(blob);
          setImageCropped(url);
        }
      },
      "image/png",
      1
    );

    //set value to ImageInput
    onChange({
      url: imageCropped,
      type: "image/png",
    });
  }, [completedCrop]);

  useEffect(() => {
    if (isImageValid) {
      ownInput.input.onChange({ url: imageCropped, type: "image/png" });
    }
  }, [imageCropped]);

  const transformData = async (data: any) => {
    if (data) {
      setIsImageValid(true);
      setIsImageCancel(false);
      //Get Blob
      let url = await getBlobFromUrl(data);
      setUpImg(url);

      //Validate the File Size.
      const validateSize = validateImageSize(data.size);
      if (!validateSize) {
        setIsImageValid(false);
        setFileCache(null);
        return notify("Max 2MB file is allowed for image.", "warning");
      }
    }
  };
  return (
    <>
      <FileInput
        {...props}
        label={label ? label : "File/Image"}
        source={`${source}File`}
        onChange={transformData}
      >
        <ImageField
          label={label ? label : "File/Image"}
          source="url"
          title="title"
        />
      </FileInput>

      {upImg && isImageValid && !isImageCancel && (
        <>
          <div className={classes.main}>
            <div className={classes.topNavsContainer}>
              <Button type="button" color="secondary" onClick={onCancel}>
                Cancel
              </Button>
            </div>

            <div>
              <ReactCrop
                src={upImg}
                onImageLoaded={onLoad}
                crop={crop}
                onChange={(c: any) => setCrop(c)}
                onComplete={(c: any) => setCompletedCrop(c)}
              />

              <div style={{ display: "none" }}>
                <canvas
                  ref={previewCanvasRef}
                  // Rounding is important so the canvas width and height matches/is a multiple for sharpness.
                  style={{
                    width: Math.round(completedCrop?.width ?? 0),
                    height: Math.round(completedCrop?.height ?? 0),
                  }}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

const getBlobFromUrl = async (blob: any) => {
  console.log("BLOB", blob);
  var reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      var base64data = reader.result;
      resolve(base64data);
    };
  });
};

export default CustomFileInput;
